/**
 * PathAnimator.ts
 * Provides path calculation functions for bounce, elastic, and wave entry animations
 *
 * These animations require JavaScript-driven frame updates because they involve
 * complex mathematical curves that can't be expressed with CSS transitions alone.
 *
 * Public API:
 * - animatePath(element, startPos, endPos, pathConfig, duration, onComplete)
 * - calculateBouncePosition(t, start, end, config)
 * - calculateElasticPosition(t, start, end, config)
 * - calculateWavePosition(t, start, end, config)
 */

import type {
  BouncePathConfig,
  ElasticPathConfig,
  WavePathConfig,
  EntryPathConfig,
  EntryPathType,
  EntryRotationConfig,
  EntryScaleConfig
} from '../config/types';
import {
  resolveBounceConfig,
  resolveElasticConfig,
  resolveWavePathConfig
} from '../config/defaults';

export interface Point {
  x: number;
  y: number;
}

export interface EntryCurrentState {
  x: number;
  y: number;
  rotation: number;
  scale: number;
}

export interface PathAnimationOptions {
  element: HTMLElement;
  startPosition: Point;
  endPosition: Point;
  pathConfig: EntryPathConfig;
  duration: number;
  imageWidth: number;
  imageHeight: number;
  rotation: number;           // Final rotation
  scale: number;              // Final scale
  onComplete?: () => void;
  onProgress?: (t: number, elapsed: number, current: EntryCurrentState) => void;
  // Rotation animation options
  rotationConfig?: EntryRotationConfig;
  startRotation?: number;     // Starting rotation (if different from final)
  // Scale animation options
  scaleConfig?: EntryScaleConfig;
  startScale?: number;        // Starting scale (if different from final)
}

/**
 * Linear interpolation helper
 */
function lerp(start: number, end: number, t: number): number {
  return start + (end - start) * t;
}

/**
 * Calculate position along a bounce path
 * Overshoot and settle animation
 */
export function calculateBouncePosition(
  t: number,
  start: Point,
  end: Point,
  config: BouncePathConfig
): Point {
  const { overshoot, bounces, decayRatio } = config;

  // Direction vector from start to end
  const dx = end.x - start.x;
  const dy = end.y - start.y;

  // Calculate keyframe timings based on number of bounces
  const keyframes = generateBounceKeyframes(bounces, decayRatio);

  // Find current segment
  let progress = 0;
  let segmentStart = 0;
  let segmentEnd = 1;
  let segmentOvershoot = overshoot;
  let isOvershootPhase = false;

  for (let i = 0; i < keyframes.length; i++) {
    if (t <= keyframes[i].time) {
      segmentStart = i === 0 ? 0 : keyframes[i - 1].time;
      segmentEnd = keyframes[i].time;
      segmentOvershoot = keyframes[i].overshoot;
      isOvershootPhase = keyframes[i].isOvershoot;
      break;
    }
  }

  // Calculate progress within current segment
  const segmentT = (t - segmentStart) / (segmentEnd - segmentStart);

  if (isOvershootPhase) {
    // Ease out into overshoot
    progress = 1 + segmentOvershoot * easeOutQuad(segmentT);
  } else if (segmentStart === 0) {
    // Initial travel to target - ease out
    progress = easeOutQuad(segmentT);
  } else {
    // Settling back from overshoot
    const prevOvershoot = keyframes.find((k, i) =>
      k.time > segmentStart && i > 0 && keyframes[i - 1].isOvershoot
    );
    const fromProgress = 1 + (prevOvershoot?.overshoot || segmentOvershoot);
    progress = lerp(fromProgress, 1, easeOutQuad(segmentT));
  }

  return {
    x: start.x + dx * progress,
    y: start.y + dy * progress
  };
}

/**
 * Generate keyframe timings for bounce animation
 */
function generateBounceKeyframes(
  bounces: number,
  decayRatio: number
): Array<{ time: number; overshoot: number; isOvershoot: boolean }> {
  const keyframes: Array<{ time: number; overshoot: number; isOvershoot: boolean }> = [];

  // Initial travel takes 60% of time
  let currentTime = 0.6;
  keyframes.push({ time: currentTime, overshoot: 0, isOvershoot: false });

  let currentOvershoot = 0.15; // Initial overshoot amount
  const remainingTime = 0.4;
  const bounceTime = remainingTime / (bounces * 2);

  for (let i = 0; i < bounces; i++) {
    // Overshoot phase
    currentTime += bounceTime;
    keyframes.push({ time: currentTime, overshoot: currentOvershoot, isOvershoot: true });

    // Settle phase
    currentTime += bounceTime;
    keyframes.push({ time: currentTime, overshoot: currentOvershoot * decayRatio, isOvershoot: false });

    currentOvershoot *= decayRatio;
  }

  // Final settle
  keyframes.push({ time: 1, overshoot: 0, isOvershoot: false });

  return keyframes;
}

/**
 * Calculate position along an elastic path
 * Spring-like oscillation animation
 */
export function calculateElasticPosition(
  t: number,
  start: Point,
  end: Point,
  config: ElasticPathConfig
): Point {
  const { stiffness, damping, mass, oscillations } = config;

  // Direction vector from start to end
  const dx = end.x - start.x;
  const dy = end.y - start.y;

  // Normalized spring physics
  // Natural frequency based on stiffness and mass
  const omega = Math.sqrt(stiffness / mass);

  // Damping ratio (normalized)
  const zeta = damping / (2 * Math.sqrt(stiffness * mass));

  // Progress using damped harmonic oscillator
  let progress: number;

  if (zeta < 1) {
    // Underdamped - oscillates
    const dampedFreq = omega * Math.sqrt(1 - zeta * zeta);
    const envelope = Math.exp(-zeta * omega * t * 3);
    const oscillation = Math.cos(dampedFreq * t * oscillations * Math.PI);
    progress = 1 - envelope * oscillation;
  } else {
    // Critically damped or overdamped - no oscillation
    progress = 1 - Math.exp(-omega * t * 3);
  }

  // Clamp progress
  progress = Math.max(0, Math.min(progress, 1.3)); // Allow slight overshoot

  return {
    x: start.x + dx * progress,
    y: start.y + dy * progress
  };
}

/**
 * Calculate position along a wave path
 * Sinusoidal path from start to end
 */
export function calculateWavePosition(
  t: number,
  start: Point,
  end: Point,
  config: WavePathConfig
): Point {
  const { amplitude, frequency, decay, decayRate, phase } = config;

  // Direction vector from start to end
  const dx = end.x - start.x;
  const dy = end.y - start.y;
  const length = Math.sqrt(dx * dx + dy * dy);

  // Perpendicular vector (normalized)
  const perpX = length > 0 ? -dy / length : 0;
  const perpY = length > 0 ? dx / length : 1;

  // Wave calculation
  const wavePhase = frequency * Math.PI * 2 * t + phase;
  const decayFactor = decay ? Math.pow(1 - t, decayRate) : 1;
  const waveOffset = amplitude * Math.sin(wavePhase) * decayFactor;

  // Ease out for smooth arrival
  const progressT = easeOutCubic(t);

  // Linear interpolation + wave offset
  return {
    x: lerp(start.x, end.x, progressT) + waveOffset * perpX,
    y: lerp(start.y, end.y, progressT) + waveOffset * perpY
  };
}

/**
 * Easing functions
 */
function easeOutQuad(t: number): number {
  return 1 - (1 - t) * (1 - t);
}

function easeOutCubic(t: number): number {
  return 1 - Math.pow(1 - t, 3);
}

/**
 * Calculate wobble rotation for a given animation progress
 */
function calculateWobbleRotation(
  progress: number,
  finalRotation: number,
  wobbleConfig: { amplitude: number; frequency: number; decay: boolean }
): number {
  const { amplitude, frequency, decay } = wobbleConfig;

  // Oscillation using sine wave
  const oscillation = Math.sin(progress * frequency * Math.PI * 2);

  // Apply decay if enabled (stronger decay toward end)
  const decayFactor = decay ? Math.pow(1 - progress, 2) : 1;

  // Calculate wobble offset
  const wobbleOffset = amplitude * oscillation * decayFactor;

  return finalRotation + wobbleOffset;
}

/**
 * Calculate pop scale for a given animation progress
 */
function calculatePopScale(
  progress: number,
  finalScale: number,
  popConfig: { overshoot: number; bounces: number }
): number {
  const { overshoot, bounces } = popConfig;

  // Generate keyframes for bounce effect
  const keyframes: Array<{ time: number; scale: number }> = [];

  // Reach overshoot at 50% of animation
  keyframes.push({ time: 0.5, scale: overshoot });

  // Add bounces
  let currentOvershoot = overshoot;
  const bounceDecay = 0.5;
  const remainingTime = 0.5;
  const bounceTime = remainingTime / (bounces * 2);

  let currentTime = 0.5;
  for (let i = 0; i < bounces; i++) {
    const undershoot = 1 - (currentOvershoot - 1) * bounceDecay;
    currentTime += bounceTime;
    keyframes.push({ time: currentTime, scale: undershoot });

    currentOvershoot = 1 + (currentOvershoot - 1) * bounceDecay * bounceDecay;
    currentTime += bounceTime;
    if (i < bounces - 1) {
      keyframes.push({ time: currentTime, scale: currentOvershoot });
    }
  }

  keyframes.push({ time: 1, scale: 1 });

  // Find current segment
  let currentScale = 1;
  for (let i = 0; i < keyframes.length; i++) {
    if (progress <= keyframes[i].time) {
      const prevTime = i === 0 ? 0 : keyframes[i - 1].time;
      const prevScale = i === 0 ? 1 : keyframes[i - 1].scale;
      const segmentProgress = (progress - prevTime) / (keyframes[i].time - prevTime);
      const easedProgress = easeOutQuad(segmentProgress);
      currentScale = prevScale + (keyframes[i].scale - prevScale) * easedProgress;
      break;
    }
  }

  return currentScale * finalScale;
}

/**
 * Animate an element along a path using requestAnimationFrame
 */
export function animatePath(options: PathAnimationOptions): void {
  const {
    element,
    startPosition,
    endPosition,
    pathConfig,
    duration,
    imageWidth,
    imageHeight,
    rotation: finalRotation,
    scale: finalScale,
    onComplete,
    onProgress,
    rotationConfig,
    startRotation,
    scaleConfig,
    startScale
  } = options;

  const pathType = pathConfig.type;

  // Determine if we need to animate rotation
  const animateRotation = startRotation !== undefined && startRotation !== finalRotation;
  const isWobbleMode = rotationConfig?.mode === 'wobble';
  const wobbleConfig = rotationConfig?.wobble || { amplitude: 15, frequency: 3, decay: true };
  const needsRotationAnimation = animateRotation || isWobbleMode;

  // Determine if we need to animate scale
  const animateScale = startScale !== undefined && startScale !== finalScale;
  const isPopMode = scaleConfig?.mode === 'pop';
  const popConfig = scaleConfig?.pop || { overshoot: 1.2, bounces: 1 };
  const needsScaleAnimation = animateScale || isPopMode;

  // For linear paths WITHOUT rotation or scale animation, use CSS transitions (handled elsewhere)
  if (pathType === 'linear' && !needsRotationAnimation && !needsScaleAnimation) {
    if (onComplete) onComplete();
    return;
  }

  const startTime = performance.now();

  // Build center offset for transform
  const centerOffsetX = -imageWidth / 2;
  const centerOffsetY = -imageHeight / 2;

  function tick(currentTime: number): void {
    const elapsed = currentTime - startTime;
    const t = Math.min(elapsed / duration, 1);

    // Calculate position based on path type
    let position: Point;

    switch (pathType) {
      case 'bounce': {
        const config = resolveBounceConfig(
          pathConfig.bouncePreset,
          pathConfig.bounce
        );
        position = calculateBouncePosition(t, startPosition, endPosition, config);
        break;
      }
      case 'elastic': {
        const config = resolveElasticConfig(
          pathConfig.elasticPreset,
          pathConfig.elastic
        );
        position = calculateElasticPosition(t, startPosition, endPosition, config);
        break;
      }
      case 'wave': {
        const config = resolveWavePathConfig(
          pathConfig.wavePreset,
          pathConfig.wave
        );
        position = calculateWavePosition(t, startPosition, endPosition, config);
        break;
      }
      default:
        position = {
          x: lerp(startPosition.x, endPosition.x, t),
          y: lerp(startPosition.y, endPosition.y, t)
        };
    }

    // Calculate translate offset from final position
    const translateX = position.x - endPosition.x;
    const translateY = position.y - endPosition.y;

    // Calculate current rotation
    let currentRotation: number;
    if (isWobbleMode) {
      currentRotation = calculateWobbleRotation(t, finalRotation, wobbleConfig);
    } else if (animateRotation) {
      currentRotation = lerp(startRotation!, finalRotation, t);
    } else {
      currentRotation = finalRotation;
    }

    // Calculate current scale
    let currentScale: number;
    if (isPopMode) {
      currentScale = calculatePopScale(t, finalScale, popConfig);
    } else if (animateScale) {
      currentScale = lerp(startScale!, finalScale, t);
    } else {
      currentScale = finalScale;
    }

    // Apply transform
    element.style.transform =
      `translate(${centerOffsetX}px, ${centerOffsetY}px) ` +
      `translate(${translateX}px, ${translateY}px) ` +
      `rotate(${currentRotation}deg) scale(${currentScale})`;

    // Fire onProgress for in-progress frames
    if (onProgress && t < 1) {
      onProgress(t, elapsed, {
        x: position.x,
        y: position.y,
        rotation: currentRotation,
        scale: currentScale,
      });
    }

    if (t < 1) {
      requestAnimationFrame(tick);
    } else {
      // Ensure we end exactly at the final position, rotation, and scale
      element.style.transform =
        `translate(${centerOffsetX}px, ${centerOffsetY}px) ` +
        `rotate(${finalRotation}deg) scale(${finalScale})`;
      if (onComplete) onComplete();
    }
  }

  requestAnimationFrame(tick);
}

/**
 * Check if a path type requires JavaScript animation (vs CSS transitions)
 */
export function requiresJSAnimation(pathType: EntryPathType): boolean {
  return pathType === 'bounce' || pathType === 'elastic' || pathType === 'wave';
}

